import React, { useState } from 'react';
import { Check, Loader2, Mail } from 'lucide-react';

import { useI18n } from '@/i18n/LanguageContext';
import { crmSubscribe } from '@/contexts/AuthContext';
import { submitForm, AUDIENCE_META, Audience } from '@/lib/crmForms';
import { TRADES, tradeLabel } from '@/data/constants';
import { smsConsentPayload } from '@/data/smsConsent';
import SmsConsentCheckbox from '@/components/SmsConsentCheckbox';

/**
 * THE reusable visitor / newsletter lead-capture form.
 *
 * One component, used everywhere we want an email (home, jobs, trade pages,
 * state pages, pricing, resources, footer-style blocks). Every submission goes to BOTH:
 *
 *   1. submitForm() -> crm-capture -> GoHighLevel contact upsert with the
 *      audience tag, the trade tag(s), the SMS consent flag and full UTM
 *      attribution. This is what starts the onboarding / nurture workflow.
 *   2. crmSubscribe() -> the platform's own subscribe endpoint (source + tags),
 *      which keeps the marketing list and the job-alert digest in sync.
 *
 * Optional `onSubmitted` runs after both CRM calls (state pages use it to
 * create the job-alert saved search) and may return a custom success message.
 */

export interface LeadCaptureFormProps {
  audience?: Audience;
  /** Where on the site the form lives — becomes the CRM `source`. */
  source: string;
  title?: string;
  subtitle?: string;
  /** Ask which trade they work in (single select). */
  askTrade?: boolean;
  /** Let the visitor pick SEVERAL trades as chips (job alerts). */
  multiTrade?: boolean;
  /** Pre-set the trade (trade landing pages). */
  trade?: string;
  /** Pre-set city/state (city, county and state landing pages). */
  city?: string;
  state?: string;
  /** Extra tags — sent to GoHighLevel AND to the CRM subscribe API. */
  tags?: string[];
  variant?: 'card' | 'inline' | 'dark';
  onDark?: boolean;
  className?: string;
  submitLabel?: string;
  /** Runs after the CRM calls. Return a string to override the success message. */
  onSubmitted?: (p: { email: string; name: string; phone: string; trades: string[] }) => Promise<string | void>;
  /** Extra content rendered above the fields (e.g. a pre-filled state badge). */
  children?: React.ReactNode;
}

const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({
  audience = 'visitor',
  source,
  title,
  subtitle,
  askTrade = false,
  multiTrade = false,
  trade,
  city,
  state,
  tags = [],
  variant = 'card',
  onDark = false,
  className = '',
  submitLabel,
  onSubmitted,
  children,
}) => {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pickedTrade, setPickedTrade] = useState(trade || '');
  const [pickedTrades, setPickedTrades] = useState<string[]>(trade ? [trade] : []);
  // A2P 10DLC: SMS consent is NEVER pre-checked. The registered opt-in flow
  // (A2P_OPT_IN_DESCRIPTION) says "voluntarily selecting an unchecked SMS
  // consent checkbox"; a pre-ticked box would contradict the campaign filing.
  const [sms, setSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const meta = AUDIENCE_META[audience];
  const dark = variant === 'dark' || onDark;

  const toggleTrade = (slug: string) =>
    setPickedTrades((cur) => (cur.includes(slug) ? cur.filter((x) => x !== slug) : [...cur, slug]));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setErr(t('auth.errEmail'));
      return;
    }
    setErr('');
    setBusy(true);

    const trades = multiTrade ? pickedTrades : [pickedTrade || trade || ''].filter(Boolean);
    const chosenTrade = trades[0] || '';
    const tradeTags = trades.map((x) => `trade:${x}`);
    const allTags = [...meta.tags, ...tags, ...tradeTags, `source:${source}`, `nurture:${meta.nurture}`];
    // SMS consent only counts when the box was ticked AND a phone was given.
    const smsGranted = sms === true && !!phone.trim();
    const cleanEmail = email.trim().toLowerCase();

    await submitForm({
      formType: 'newsletter',
      email: cleanEmail,
      name: name.trim() || undefined,
      phone: phone.trim() || undefined,
      language: lang,
      userType: meta.userType,
      ...smsConsentPayload(smsGranted),
      consent: true,
      city,
      state,
      trade: chosenTrade || undefined,
      tradeSlug: chosenTrade || undefined,
      tradeLabel: chosenTrade ? trades.map((x) => tradeLabel(x, lang)).join(', ') : undefined,
      tags: allTags,
      fields: { signup_source: source, audience },
    });

    await crmSubscribe({
      email: cleanEmail,
      name: name.trim() || undefined,
      phone: phone.trim() || undefined,
      sms_opt_in: smsGranted,
      source,
      tags: [...new Set(['newsletter', `audience:${audience}`, ...tags, ...tradeTags, `lang:${lang}`])],
      language: lang,
    });

    let msg: string | void = undefined;
    if (onSubmitted) {
      try {
        msg = await onSubmitted({ email: cleanEmail, name: name.trim(), phone: phone.trim(), trades });
      } catch {
        msg = undefined;
      }
    }

    setBusy(false);
    setDone(msg || t('home.newsletterOk'));
    setEmail('');
    setName('');
    setPhone('');
  };

  const inputCls = variant === 'dark'
    ? 'w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none'
    : 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-orange-500 focus:outline-none';

  const wrapCls =
    variant === 'card'
      ? 'rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'
      : variant === 'dark'
      ? 'rounded-2xl border border-white/10 bg-slate-900 p-6'
      : '';

  if (done) {
    return (
      <div className={`${wrapCls} ${className}`}>
        <p
          role="status"
          className={`flex items-start gap-2 rounded-lg px-3 py-3 text-sm font-semibold ${
            dark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          <Check className="mt-0.5 h-4 w-4 shrink-0" /> {done}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`${wrapCls} ${className}`}>
      <p className={`flex items-center gap-2 text-base font-bold ${dark ? 'text-white' : 'text-slate-900'}`}>
        <Mail className={`h-4 w-4 ${dark ? 'text-orange-400' : 'text-orange-500'}`} />
        {title || t('home.newsletterTitle')}
      </p>
      <p className={`mt-1 text-sm ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
        {subtitle || t('home.newsletterSub')}
      </p>

      {children}

      <div className="mt-4 space-y-2">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('common.name')} autoComplete="name" className={inputCls} />
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('common.email')} autoComplete="email" className={inputCls} />
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t('home.phonePlaceholder')} autoComplete="tel" className={inputCls} />

        {askTrade && !multiTrade && (
          <select value={pickedTrade} onChange={(e) => setPickedTrade(e.target.value)} className={inputCls}>
            <option value="">{t('common.trade')}</option>
            {TRADES.map((tr) => (
              <option key={tr.slug} value={tr.slug}>{tradeLabel(tr.slug, lang)}</option>
            ))}
          </select>
        )}

        {multiTrade && (
          <fieldset>
            <legend className={`mb-1.5 text-xs font-bold uppercase tracking-wide ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
              {lang === 'es' ? 'Oficios (opcional — vacío = todos)' : 'Trades (optional — none = all trades)'}
            </legend>
            <div className="flex flex-wrap gap-1.5">
              {TRADES.map((tr) => {
                const on = pickedTrades.includes(tr.slug);
                return (
                  <button
                    key={tr.slug}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleTrade(tr.slug)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                      on
                        ? 'border-orange-500 bg-orange-500 text-white'
                        : dark
                        ? 'border-white/15 text-slate-300 hover:border-orange-400'
                        : 'border-slate-300 text-slate-700 hover:border-orange-400'
                    }`}
                  >
                    {tradeLabel(tr.slug, lang)}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        {/* A2P 10DLC: optional and UNCHECKED by default; never blocks submit. */}
        <SmsConsentCheckbox checked={sms} onChange={setSms} variant={dark ? 'dark' : 'light'} />

        {err && <p className="text-xs font-semibold text-red-500">{err}</p>}

        <button
          type="submit"
          disabled={busy}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitLabel || t('common.submit')}
        </button>
      </div>
    </form>
  );
};

export default LeadCaptureForm;
